import React from "react";

/**
 * Pure render helpers for Succession Kit Stage Four documents.
 * All documents wrap their body in the .ecclesiastical-document class so the
 * shared print styles apply for browser print / save-as-PDF.
 */

export const STANDING_FOOTER =
  "This is a sample document for education. Have a licensed professional review and adapt it to your state and facts before signing. Complete every blank. Never backdate. File the signed original in the Record.";

export interface RenderContext {
  unaName: string;
  answers: Record<string, any>;
  branchFlags: Record<string, boolean>;
  decisions: Record<string, any>;
}

const TRIGGERS = [
  "Death of the officer.",
  "Incapacity of the officer, determined under the Incapacity Protocol.",
  "Written resignation of the officer.",
  "A resolution of the Executive Board activating this designation.",
];

const NO_OWNERSHIP =
  "This paper passes responsibility, not ownership. Nobody inherits anything through it. It gives no share of the Association, no income, and no property right. It is not a will and it is not a trust. It only names who carries the office for governance to continue.";

const REVOCATION =
  "This paper stays in force unless it is canceled in writing, either by a notarized paper signed by the officer, or by a full vote of the Executive Board under the Constitution.";

function DocFrame({
  title,
  unaName,
  children,
}: {
  title: string;
  unaName: string;
  children: React.ReactNode;
}) {
  return (
    <div className="ecclesiastical-document bg-white p-8 border rounded-md text-black">
      <div className="text-center mb-6">
        <h3 className="text-xl font-serif uppercase tracking-wide">{unaName || "The Association"}</h3>
        <p className="text-xs italic mt-1">[CA filing number: __________] [EIN: __________] [Address: __________]</p>
        <h4 className="text-lg font-serif mt-3">{title}</h4>
      </div>
      {children}
      <div className="mt-10 pt-4 border-t border-black text-xs italic text-center">
        {STANDING_FOOTER}
      </div>
    </div>
  );
}

function DateLine({ label = "Date" }: { label?: string }) {
  return (
    <div>
      <div className="border-b border-black h-8" />
      <p className="text-sm mt-1">{label}</p>
    </div>
  );
}

function SigLine({ label }: { label: string }) {
  return (
    <div>
      <div className="border-b border-black h-8" />
      <p className="text-sm mt-1">{label}</p>
    </div>
  );
}

function NotaryBlock() {
  return (
    <div className="notary-box mt-10 p-4 border border-black">
      <p className="text-center font-bold mb-3">NOTARY ACKNOWLEDGMENT</p>
      <p className="mb-2">State of ______________________</p>
      <p className="mb-2">County of _____________________</p>
      <p className="mb-2">
        On ______________, 20____, before me personally appeared ______________________, who proved to me on the basis of satisfactory evidence to be the person whose name is subscribed to the within instrument and acknowledged that they executed the same in their authorized capacity.
      </p>
      <p className="mb-4">I certify under PENALTY OF PERJURY under the laws of the State that the foregoing paragraph is true and correct.</p>
      <p>WITNESS my hand and official seal.</p>
      <div className="mt-6 grid grid-cols-2 gap-8">
        <SigLine label="Notary signature" />
        <div>
          <div className="border border-black h-16 w-32" />
          <p className="text-sm mt-1">Seal</p>
        </div>
      </div>
    </div>
  );
}

function orderList(list: unknown, fallback: string): string[] {
  const arr = Array.isArray(list) ? (list as string[]).filter((v) => v && v.trim()) : [];
  return arr.length ? arr : [fallback];
}

function declineText(gap: string): string {
  if (gap === "board_resolves") return "If a named Successor Steward says no, is not available, or has died before the seat opens, the Executive Board decides at that time by a written decision.";
  return "If a named Successor Steward says no, is not available, or has died before the seat opens, the next name on the list steps up.";
}

/* ---------------- 1. Continuity Resolution ---------------- */

export function ContinuityResolutionDoc({ ctx }: { ctx: RenderContext }) {
  const { unaName, answers, decisions } = ctx;
  const chiefName = answers.chief_officer_name || "____________________";
  const secName = answers.secretary_treasurer_name || "____________________";
  const chiefList = orderList(decisions?.order?.chief, answers.successor_for_chief || "____________________");
  const secList = orderList(decisions?.order?.secretary, answers.successor_for_secretary || "____________________");
  const gap = decisions?.order?.gap || "next_in_order";

  return (
    <DocFrame title="Private Stewardship Continuity Resolution" unaName={unaName}>
      <p className="mb-3"><strong>Date and time:</strong> ______________, 20____ at ______ __.M.</p>
      <p className="mb-3"><strong>Place:</strong> ______________________________</p>
      <p className="mb-3"><strong>Designating officer:</strong> {chiefName}, Principal Executive Officer</p>

      <p className="mb-3"><strong>WHEREAS,</strong> the Association is a private body governed by its Constitution, and the undersigned holds designating authority for the offices named below; and</p>
      <p className="mb-3"><strong>WHEREAS,</strong> the undersigned wishes to designate Successor Stewards in order, so that governance continues without interruption at death, incapacity, resignation, or Board activation;</p>

      <p className="mb-2"><strong>NOW, THEREFORE, BE IT RESOLVED,</strong> that the following persons are designated Successor Stewards to the office of <strong>Principal Executive Officer</strong>, currently held by {chiefName}, in the order listed:</p>
      <ol className="list-decimal pl-8 space-y-1 mb-4">
        {chiefList.map((n, i) => <li key={i}>{n}, Successor Steward to the Principal Executive Officer</li>)}
      </ol>

      <p className="mb-2"><strong>BE IT FURTHER RESOLVED,</strong> that the following persons are designated Successor Stewards to the office of <strong>Secretary Treasurer</strong>, currently held by {secName}, in the order listed:</p>
      <ol className="list-decimal pl-8 space-y-1 mb-4">
        {secList.map((n, i) => <li key={i}>{n}, Successor Steward to the Secretary Treasurer</li>)}
      </ol>

      <p className="mb-3"><strong>Decline rule.</strong> {declineText(gap)}</p>

      <p className="mb-2"><strong>Triggers.</strong> This designation takes effect upon any of the following:</p>
      <ol className="list-decimal pl-8 space-y-1 mb-4">
        {TRIGGERS.map((t, i) => <li key={i}>{t}</li>)}
      </ol>

      <p className="mb-3"><strong>Revocation.</strong> {REVOCATION}</p>
      <p className="mb-6"><strong>No ownership.</strong> {NO_OWNERSHIP}</p>

      <div className="mt-10 grid grid-cols-2 gap-8">
        <SigLine label={`${chiefName}, Principal Executive Officer`} />
        <DateLine />
      </div>

      <NotaryBlock />
    </DocFrame>
  );
}

/* ---------------- 2. Standing Successor Resolution ---------------- */

export function StandingSuccessorResolutionDoc({ ctx }: { ctx: RenderContext }) {
  const { unaName, answers, decisions } = ctx;
  const chiefList = orderList(decisions?.order?.chief, answers.successor_for_chief || "____________________");
  const secList = orderList(decisions?.order?.secretary, answers.successor_for_secretary || "____________________");
  const gap = decisions?.order?.gap || "next_in_order";

  return (
    <DocFrame title="Standing Successor Resolution" unaName={unaName}>
      <h5 className="font-serif text-base mb-2">Why we are doing this</h5>
      <p className="mb-4">
        We adopted a Private Stewardship Continuity Resolution that names the Successor Stewards for the Association. We keep a fresh standing record of that order, and we update it when names or order change.
      </p>

      <h5 className="font-serif text-base mb-2">What the Board decides</h5>
      <ol className="list-decimal pl-6 space-y-3 mb-6">
        <li>
          <p>The current successor order for the office of <strong>Principal Executive Officer</strong> is:</p>
          <ol className="list-decimal pl-8 mt-2 space-y-1">
            {chiefList.map((n, i) => <li key={i}>{n}</li>)}
          </ol>
        </li>
        <li>
          <p>The current successor order for the office of <strong>Secretary Treasurer</strong> is:</p>
          <ol className="list-decimal pl-8 mt-2 space-y-1">
            {secList.map((n, i) => <li key={i}>{n}</li>)}
          </ol>
        </li>
        <li>{declineText(gap)}</li>
        <li>This paper takes effect on ______________, 20____.</li>
        <li>This paper replaces any earlier Standing Successor Resolution and is filed in the Record.</li>
      </ol>

      <div className="mt-10 grid grid-cols-2 gap-8">
        <SigLine label="Principal Executive Officer" />
        <SigLine label="Secretary Treasurer" />
      </div>
      <div className="mt-6 grid grid-cols-2 gap-8">
        <SigLine label="Board Member" />
        <SigLine label="Board Member" />
      </div>
    </DocFrame>
  );
}

/* ---------------- 3. Incapacity Protocol ---------------- */

function incapWhoText(d: any): string {
  const who = d?.incapacity?.who;
  const trusted = d?.incapacity?.trusted_name;
  const other = d?.incapacity?.who_other;
  if (who === "two_licensed") return "Two licensed medical professionals put their opinion in writing that the officer cannot serve.";
  if (who === "board_supermajority") return "A supermajority of the Executive Board decides at a called meeting that the officer cannot serve.";
  if (who === "trusted_person") return `${trusted || "____________________"}, a trusted person the officer named, writes a signed statement that the officer cannot serve.`;
  if (who === "other" && other) return `Found this way: ${other}`;
  return "Found this way: ____________________ [write in your own rule].";
}

function incapWaitText(d: any): string {
  const wait = d?.incapacity?.wait;
  const other = d?.incapacity?.wait_other;
  if (wait === "immediate") return "The plan turns on the same day.";
  if (wait === "seven_days") return "The plan turns on seven days after, so there is time to look again.";
  if (wait === "thirty_days") return "The plan turns on thirty days after.";
  if (wait === "other" && other) return `Turns on this way: ${other}`;
  return "Turns on this way: ____________________ [write in your own rule].";
}

export function IncapacityProtocolDoc({ ctx }: { ctx: RenderContext }) {
  const { unaName, decisions } = ctx;
  return (
    <DocFrame title="Incapacity Protocol" unaName={unaName}>
      <h5 className="font-serif text-base mb-2">Why we are doing this</h5>
      <p className="mb-4">
        This paper says what incapacity means for the officers of the Association, and how the Continuity Resolution turns on when incapacity is found. Incapacity means a season when an officer cannot serve because of health. A rule set in calm never becomes an argument in a hard moment.
      </p>

      <h5 className="font-serif text-base mb-2">What the Board decides</h5>
      <ol className="list-decimal pl-6 space-y-3 mb-6">
        <li>
          <p><strong>How incapacity is found.</strong> {incapWhoText(decisions)}</p>
        </li>
        <li>
          <p><strong>Waiting rule.</strong> {incapWaitText(decisions)}</p>
        </li>
        <li>
          <p><strong>Turning it on.</strong> When incapacity is found under this paper, the Private Stewardship Continuity Resolution turns on and the named Successor Stewards step into their offices in order.</p>
        </li>
        <li>
          <p><strong>Coming back.</strong> If the officer heals and can serve again, the Board can seat them back in the office by a written decision that says so.</p>
        </li>
      </ol>

      <div className="mt-10 grid grid-cols-2 gap-8">
        <SigLine label="Principal Executive Officer" />
        <DateLine />
      </div>
      <div className="mt-6 grid grid-cols-2 gap-8">
        <SigLine label="Secretary Treasurer" />
        <DateLine />
      </div>
    </DocFrame>
  );
}

/* ---------------- 4. Founder's Letter ---------------- */

export const FOUNDERS_PROMPTS: { key: string; label: string; helper: string }[] = [
  { key: "why_exists", label: "Why this Association exists.", helper: "Say it in your own voice." },
  { key: "hope_becomes", label: "What I hope it becomes.", helper: "Speak to the horizon." },
  { key: "mission_family", label: "What the mission means to our family.", helper: "Name it plainly." },
  { key: "protect", label: "What I ask you to protect.", helper: "What must not change." },
  { key: "release", label: "What I release you to change.", helper: "What you trust them to move." },
  { key: "blessing", label: "A word of blessing.", helper: "The last thing you want them to carry." },
];

export function FoundersLetterDoc({ ctx, content }: { ctx: RenderContext; content: Record<string, string> }) {
  const { unaName, answers } = ctx;
  const chiefName = answers.chief_officer_name || "____________________";
  return (
    <DocFrame title="The Founder's Letter" unaName={unaName}>
      <p className="mb-4 italic">To the one who comes after me,</p>
      {FOUNDERS_PROMPTS.map((p) => {
        const text = (content?.[p.key] || "").trim();
        return (
          <div key={p.key} className="mb-5">
            <p className="font-semibold mb-1">{p.label}</p>
            <p className="whitespace-pre-wrap">{text || "____________________________________________"}</p>
          </div>
        );
      })}
      <div className="mt-10 grid grid-cols-2 gap-8">
        <SigLine label={chiefName} />
        <DateLine />
      </div>
    </DocFrame>
  );
}

/* ---------------- 5. Successor's Manual ---------------- */

const ASSET_HANDOFF: Record<string, string> = {
  assoc_bank: "Association bank accounts: each institution has its own letter package prepared in the Execution stage.",
  llc_bank: "Company bank accounts: the successor manager acceptance is filed with the company records; the bank follows the acceptance and the certificate of incumbency.",
  brokerage: "Brokerage or custody accounts: each firm has its own transfer path in the institution register.",
  digital_hw: "Digital assets in hardware custody: seed storage locations are documented outside this portal. Access follows the planned paths only.",
  private_trust: "Private trust: your trust has its own succession terms. Review both together with a licensed professional.",
  operating_business: "Operating business: the Operating Agreement continues the manager, and the successor manager acceptance is on file.",
  real_property: "Real property: insurance carrier and any property manager are on the notification list.",
  portal_records: "Portal and records: your governance record lives in the Record inside this portal; the signed originals live where your steward filed them.",
};

const SEVEN_UNDERSTANDINGS = [
  "The mission governs. Every decision serves what the Association exists to do.",
  "Stewardship, not ownership. You hold what has been entrusted; you do not own it.",
  "The private field is real. Members of the Association are members, not customers or the public.",
  "The Record is the memory. If it is not written, it did not happen.",
  "Continuity is a practice. The plan is exercised, not stored.",
  "The office holds the authority. The person holds the office for a season.",
  "Route what must be routed. Regulated matters go to a licensed professional.",
];

export function SuccessorsManualDoc({ ctx }: { ctx: RenderContext }) {
  const { unaName, answers, decisions } = ctx;
  const chiefName = answers.chief_officer_name || "____________________";
  const secName = answers.secretary_treasurer_name || "____________________";
  const chiefList = orderList(decisions?.order?.chief, answers.successor_for_chief || "____________________");
  const secList = orderList(decisions?.order?.secretary, answers.successor_for_secretary || "____________________");
  const gap = decisions?.order?.gap || "next_in_order";
  const assets: string[] = Array.isArray(answers.asset_classes) ? answers.asset_classes : [];

  return (
    <DocFrame title="The Successor's Manual" unaName={unaName}>
      <p className="text-sm italic mb-6">The handbook you open on day one. Nothing here is a password, a key, or the location of anything secret.</p>

      <h5 className="font-semibold mt-4 mb-2">What this Association is</h5>
      <p className="mb-4">This is a private body. Stewardship, not ownership. The mission governs everything.</p>

      <h5 className="font-semibold mt-4 mb-2">The offices and who holds them today</h5>
      <ul className="list-disc pl-6 mb-4">
        <li>Principal Executive Officer: {chiefName}</li>
        <li>Secretary Treasurer: {secName}</li>
      </ul>

      <h5 className="font-semibold mt-4 mb-2">The order of succession</h5>
      <p className="mb-1"><strong>Principal Executive Officer.</strong></p>
      <ol className="list-decimal pl-8 mb-3">{chiefList.map((n, i) => <li key={i}>{n}</li>)}</ol>
      <p className="mb-1"><strong>Secretary Treasurer.</strong></p>
      <ol className="list-decimal pl-8 mb-4">{secList.map((n, i) => <li key={i}>{n}</li>)}</ol>
      <p className="mb-4"><em>Decline rule.</em> {declineText(gap)}</p>

      <h5 className="font-semibold mt-4 mb-2">The triggers and the incapacity rule</h5>
      <ul className="list-disc pl-6 mb-2">{TRIGGERS.map((t, i) => <li key={i}>{t}</li>)}</ul>
      <p className="mb-4">{incapWhoText(decisions)} {incapWaitText(decisions)}</p>

      <h5 className="font-semibold mt-4 mb-2">Where the Record lives</h5>
      <p className="mb-4">Your governance record lives in the Record inside this portal, and the signed originals live where your steward filed them.</p>

      <h5 className="font-semibold mt-4 mb-2">The banks</h5>
      <p className="mb-4">Each institution has its own path prepared. The register in the portal lists every institution and which path it uses. Walk in with the letter package for that institution and your identification.</p>

      <h5 className="font-semibold mt-4 mb-2">The asset classes this family touches</h5>
      {assets.length === 0 ? (
        <p className="mb-4 italic">No asset classes were flagged in the profile.</p>
      ) : (
        <ul className="list-disc pl-6 mb-4">
          {assets.map((a) => (
            <li key={a}>{ASSET_HANDOFF[a] || a}</li>
          ))}
        </ul>
      )}

      <h5 className="font-semibold mt-4 mb-2">The first ninety days as successor</h5>
      <ol className="list-decimal pl-6 mb-4">
        <li>Confirm the vacancy by resolution.</li>
        <li>Update banking the same week.</li>
        <li>Review the credential map with the escrow rules.</li>
        <li>Hold a meeting.</li>
        <li>Continue the rhythm.</li>
      </ol>

      <h5 className="font-semibold mt-4 mb-2">The standard you carry</h5>
      <ol className="list-decimal pl-6 mb-4">
        {SEVEN_UNDERSTANDINGS.map((u, i) => <li key={i} className="mb-1">{u}</li>)}
      </ol>

      <h5 className="font-semibold mt-4 mb-2">Who walks with you</h5>
      <p className="mb-2">Your licensed professionals, your community, and the platform's support. Route what must be routed. You do not carry this alone.</p>
    </DocFrame>
  );
}

/* ---------------- 6. Successor Manager Acceptance ---------------- */

export function SuccessorManagerAcceptanceDoc({ ctx }: { ctx: RenderContext }) {
  const { unaName, answers, decisions } = ctx;
  const chiefList = orderList(decisions?.order?.chief, answers.successor_for_chief || "____________________");
  const firstSuccessor = chiefList[0];
  return (
    <DocFrame title="Successor Manager Acceptance" unaName={unaName}>
      <p className="mb-3">Company: ______________________________ (the "Company"), a limited liability company whose sole Member is the Association.</p>

      <p className="mb-3">I, <strong>{firstSuccessor}</strong>, accept appointment as successor Manager of the Company, effective automatically upon the removal, resignation, incapacity, or death of the current Manager, in accordance with the continuity and pre-authorization provisions of the Company's Operating Agreement.</p>

      <p className="mb-3">I acknowledge that the Manager's duties run under the Member's governing documents, and I agree to perform them faithfully.</p>

      <p className="mb-3">I have read the Operating Agreement, the current Manager's certification of authority, and the Company's Certificate of Incumbency. I accept the office subject to those terms.</p>

      <p className="mb-6">This Acceptance is filed with the Company's records and becomes operative only upon the triggering event described above.</p>

      <div className="mt-10 grid grid-cols-2 gap-8">
        <SigLine label={`${firstSuccessor}, successor Manager`} />
        <DateLine />
      </div>

      <NotaryBlock />
    </DocFrame>
  );
}

/* ---------------- 7. Readiness Transition Resolution ---------------- */

const READINESS_MARK_LABELS: Record<string, string> = {
  path_complete: "The young steward has completed the Successor's Path in this portal.",
  board_confirms: "The Executive Board has confirmed readiness by resolution.",
  bridge_confirms: "The bridge successor has confirmed the handoff is right.",
  professional_confirms: "A licensed professional named in the plan has confirmed no gating concerns.",
};

export function ReadinessTransitionResolutionDoc({ ctx }: { ctx: RenderContext }) {
  const { unaName, answers, decisions } = ctx;
  const young = answers.successor_for_chief || "____________________";
  const bridge = answers.bridge_successor_name || "____________________";
  const age = decisions?.minor_plan?.readiness_age ?? 18;
  const marks: string[] = decisions?.minor_plan?.readiness_marks || [];

  return (
    <DocFrame title="Readiness Transition Resolution (prepared in advance)" unaName={unaName}>
      <p className="text-sm italic mb-4">Written today. Keep it unsigned until the day comes. Then adopt it in a meeting and file it.</p>

      <h5 className="font-serif text-base mb-2">Why we are doing this</h5>
      <p className="mb-3">
        {young} was named a young Successor Steward in our Private Stewardship Continuity Resolution. {bridge} was named the bridge successor, who holds the office until the readiness signs are met. Today, the Board looks at the readiness signs and confirms whether they have been met.
      </p>
      <p className="mb-2">Readiness signs we set:</p>
      <ol className="list-decimal pl-8 mb-4">
        <li>Age at least {age}.</li>
        {marks.map((m) => <li key={m}>{READINESS_MARK_LABELS[m] || m}</li>)}
      </ol>

      <h5 className="font-serif text-base mb-2">What the Board decides</h5>
      <ol className="list-decimal pl-6 space-y-2 mb-6">
        <li>The Board confirms each readiness sign above has been met as of ______________, 20____.</li>
        <li>The Board thanks {bridge} for faithful service as bridge successor, and honors the trust held during that season.</li>
        <li>{young} is seated in the office starting on the day this paper is adopted, and shall carry it under the Constitution and the standard of this Association.</li>
      </ol>

      <div className="mt-10 grid grid-cols-2 gap-8">
        <SigLine label="Principal Executive Officer" />
        <SigLine label="Secretary Treasurer" />
      </div>
      <div className="mt-6 grid grid-cols-2 gap-8">
        <SigLine label={bridge} />
        <SigLine label={young} />
      </div>
    </DocFrame>
  );
}

/* ---------------- Teaching content ---------------- */

export const DOC_TEACHING: Record<string, { title: string; body: string }> = {
  continuity_resolution: {
    title: "Private Stewardship Continuity Resolution",
    body: "This is the spine of your plan. It names your Successor Stewards in order and takes effect at death, incapacity, resignation, or a Board vote. It gives no ownership, no estate interest, and no inheritance. It is not a will and not a trust. It is a naming of Stewardship authority for governance continuity only.",
  },
  standing_successor_resolution: {
    title: "Standing Successor Resolution",
    body: "Your Constitution's record of who stands next, kept current. When names or order change, you adopt a fresh one and file it.",
  },
  incapacity_protocol: {
    title: "Incapacity Protocol",
    body: "A trigger defined in calm never becomes an argument in crisis. This writes down what incapacity means for your Association, who determines it, and what happens when they do.",
  },
  founders_letter: {
    title: "The Founder's Letter",
    body: "Every other document in this kit is governance. This one is voice. Your successor inherits a calling, not just a checklist, and this letter is where you hand it to them in your own words. Nothing here is legal language. Write like you talk.",
  },
  successors_manual: {
    title: "The Successor's Manual",
    body: "The handbook your successor opens on day one. It is assembled from everything you have already answered, and it never contains a password, a key, or a location of anything secret.",
  },
  successor_manager_acceptance: {
    title: "Successor Manager Acceptance",
    body: "Your company's Operating Agreement continues the manager automatically and allows a pre-signed successor acceptance kept in the records, so the company never stalls for a single day.",
  },
  readiness_transition_resolution: {
    title: "Readiness Transition Resolution (prepared in advance)",
    body: "Prepared today, adopted on the day your young steward is ready. It confirms the readiness conditions you chose and passes the office from the bridge successor with honor.",
  },
};

export const DOC_GUIDANCE: Record<string, string> = {
  continuity_resolution: "Sign in blue ink. Notarize. Scan. File in the Record. Keep the original with your governing documents.",
  standing_successor_resolution: "Adopt in a meeting. Minute it. File it.",
  incapacity_protocol: "Sign, file, and give a copy to the people named in your determination method.",
  founders_letter: "Handwrite or sign it. Seal a copy with your continuity documents. Consider reading it aloud to your successor while you can.",
  successors_manual: "Print, review with your steward, and file in the Record. Refresh it whenever names or answers change.",
  successor_manager_acceptance: "Successor signs in blue ink before a notary. File with the company records.",
  readiness_transition_resolution: "Hold this unsigned until the day arrives. Adopt it in a meeting. File it.",
};
